import { FormEvent, useEffect, useRef, useState } from 'react';
import { Event, EventCatalogs, EventFormValues } from '../types';
import { mockBackend } from '../services/mockBackend';
import { htmlToText, sanitizeEventDescriptionHTML } from '../utils/sanitizeHtml';
import { userFacingError } from '../utils/errors';

type EventFormProps = {
  userId: string;
  catalogs: EventCatalogs;
  initialEvent?: Event | null;
  onSaved: (event: Event) => void;
  onCancelEdit?: () => void;
};

const defaultValues: EventFormValues = {
  title: '',
  description: '',
  descriptionFormat: 'plain',
  startAt: '',
  endAt: '',
  location: '',
  institution: '',
  promoter: '',
  format: 'presencial',
  targetAudience: '',
  estimatedAudience: 1,
  accessType: 'aberto',
  responsibilityAccepted: false,
  registrationLink: '',
  checkedCompatibility: true,
};

const EVENT_TEXT_FIELD_LIMIT = 255;
const EVENT_URL_FIELD_LIMIT = 2048;
const HTTP_URL_PATTERN = /^https?:\/\/\S+$/i;

type LimitedEventTextField = 'title' | 'location' | 'institution' | 'promoter' | 'targetAudience';

const limitedEventTextFields: LimitedEventTextField[] = [
  'title',
  'location',
  'institution',
  'promoter',
  'targetAudience',
];

const limitedEventTextFieldLabels: Record<LimitedEventTextField, string> = {
  title: 'Título',
  location: 'Local',
  institution: 'Instituição',
  promoter: 'Promotor',
  targetAudience: 'Público',
};

const isDefaultDraft = (values: EventFormValues) =>
  JSON.stringify(values) === JSON.stringify(defaultValues);

const countCharacters = (value: string) => Array.from(value).length;

const getLimitOverflow = (value: string) => Array.from(value).slice(EVENT_TEXT_FIELD_LIMIT).join('');

const getLimitedFieldErrors = (values: EventFormValues) =>
  limitedEventTextFields.filter((field) => countCharacters(values[field]) > EVENT_TEXT_FIELD_LIMIT);

const escapeHTML = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const plainTextToHTML = (value: string) => {
  const paragraphs = value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return '';
  return paragraphs
    .map((paragraph) => `<p>${escapeHTML(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const loadDraft = (storageKey: string) => {
  if (typeof window === 'undefined') return defaultValues;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaultValues;
    return { ...defaultValues, ...JSON.parse(raw) } as EventFormValues;
  } catch {
    return defaultValues;
  }
};

type LimitedTextInputProps = {
  id: string;
  label: string;
  field: LimitedEventTextField;
  value: string;
  onChange: (field: LimitedEventTextField, value: string) => void;
  className?: string;
  list?: string;
  required?: boolean;
};

const LimitedTextInput = ({
  id,
  label,
  field,
  value,
  onChange,
  className,
  list,
  required = true,
}: LimitedTextInputProps) => {
  const characterCount = countCharacters(value);
  const isOverLimit = characterCount > EVENT_TEXT_FIELD_LIMIT;
  const overflow = isOverLimit ? getLimitOverflow(value) : '';
  const errorId = `${id}-limit-error`;

  return (
    <div className={className}>
      <label className="form-label text-white" htmlFor={id}>
        {label}
      </label>
      <div className="limited-field">
        <input
          id={id}
          list={list}
          className={`form-control input-contrast limited-field-control${isOverLimit ? ' is-invalid' : ''}`}
          value={value}
          onChange={(currentEvent) => onChange(field, currentEvent.target.value)}
          aria-invalid={isOverLimit}
          aria-describedby={isOverLimit ? errorId : undefined}
          required={required}
        />
        <span className={`limited-field-counter${isOverLimit ? ' limited-field-counter-danger' : ''}`}>
          {characterCount}/{EVENT_TEXT_FIELD_LIMIT}
        </span>
      </div>
      {isOverLimit && (
        <div id={errorId} className="limited-field-overflow">
          Excedente: <mark>{overflow}</mark>
        </div>
      )}
    </div>
  );
};

const EventForm = ({ userId, catalogs, initialEvent, onSaved, onCancelEdit }: EventFormProps) => {
  const storageKey = `frog-event-form-draft-v1:${userId}`;
  const richEditorRef = useRef<HTMLDivElement | null>(null);
  const [form, setForm] = useState<EventFormValues>(() => loadDraft(storageKey));
  const [editorKey, setEditorKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const updateField = (
    field: keyof EventFormValues,
    value: EventFormValues[keyof EventFormValues]
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFeedback(null);
  };

  useEffect(() => {
    if (!initialEvent) {
      setForm(loadDraft(storageKey));
      setFeedback(null);
      setEditorKey((current) => current + 1);
      return;
    }

    setForm({
      title: initialEvent.title,
      description: initialEvent.description,
      descriptionFormat: initialEvent.descriptionFormat || 'plain',
      startAt: initialEvent.startAt.slice(0, 16),
      endAt: initialEvent.endAt.slice(0, 16),
      location: initialEvent.location,
      institution: initialEvent.institution,
      promoter: initialEvent.promoter,
      format: initialEvent.format,
      targetAudience: initialEvent.targetAudience,
      estimatedAudience: initialEvent.estimatedAudience || 1,
      accessType: initialEvent.accessType || 'aberto',
      responsibilityAccepted: true,
      registrationLink: initialEvent.registrationLink || '',
      checkedCompatibility: true,
    });
    setFeedback(null);
    setEditorKey((current) => current + 1);
  }, [initialEvent, storageKey]);

  useEffect(() => {
    if (initialEvent || typeof window === 'undefined') return;

    if (isDefaultDraft(form)) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(form));
  }, [form, initialEvent, storageKey]);

  const formTitle = initialEvent ? 'Editar evento' : 'Novo evento';

  const syncRichDescription = () => {
    const value = richEditorRef.current?.innerHTML || '';
    updateField('description', sanitizeEventDescriptionHTML(value));
  };

  const applyRichCommand = (command: string, value?: string) => {
    richEditorRef.current?.focus();
    document.execCommand(command, false, value);
    syncRichDescription();
  };

  const insertLink = () => {
    const url = window.prompt('URL do link');
    if (!url) return;
    if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
      setFeedback({ kind: 'error', message: 'Use links iniciados por http://, https:// ou mailto:.' });
      return;
    }

    applyRichCommand('createLink', url);
  };

  const insertImage = () => {
    const url = window.prompt('URL da imagem');
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      setFeedback({ kind: 'error', message: 'Use imagens publicadas com URL http:// ou https://.' });
      return;
    }

    applyRichCommand('insertImage', url);
  };

  const changeDescriptionFormat = (descriptionFormat: EventFormValues['descriptionFormat']) => {
    setForm((current) => {
      if (current.descriptionFormat === descriptionFormat) return current;

      const description =
        descriptionFormat === 'html'
          ? plainTextToHTML(current.description)
          : htmlToText(current.description);

      return { ...current, descriptionFormat, description };
    });
    setFeedback(null);
    setEditorKey((current) => current + 1);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    const limitedFieldErrors = getLimitedFieldErrors(form);
    if (limitedFieldErrors.length > 0) {
      const firstInvalidField = limitedFieldErrors[0];
      const fieldList = limitedFieldErrors.map((field) => limitedEventTextFieldLabels[field]).join(', ');
      setFeedback({
        kind: 'error',
        message: `Revise os campos destacados: ${fieldList} devem ter no máximo ${EVENT_TEXT_FIELD_LIMIT} caracteres.`,
      });
      document.getElementById(`event-${firstInvalidField}`)?.focus();
      return;
    }

    const registrationLink = form.registrationLink.trim();
    if (registrationLink && registrationLink.length > EVENT_URL_FIELD_LIMIT) {
      setFeedback({
        kind: 'error',
        message: `Link de inscrição deve ter no máximo ${EVENT_URL_FIELD_LIMIT} caracteres.`,
      });
      document.getElementById('event-registrationLink')?.focus();
      return;
    }
    if (registrationLink && !HTTP_URL_PATTERN.test(registrationLink)) {
      setFeedback({
        kind: 'error',
        message: 'Informe uma URL válida começando com http:// ou https://.',
      });
      document.getElementById('event-registrationLink')?.focus();
      return;
    }

    if (!form.responsibilityAccepted) {
      setFeedback({
        kind: 'error',
        message: 'Confirme a declaração de responsabilidade antes de enviar.',
      });
      return;
    }

    setSubmitting(true);
    setFeedback(null);

    try {
      const description =
        form.descriptionFormat === 'html'
          ? sanitizeEventDescriptionHTML(form.description)
          : form.description.trim();

      const normalizedPayload: EventFormValues = {
        ...form,
        description,
        registrationLink,
        estimatedAudience: Number(form.estimatedAudience),
        checkedCompatibility: true,
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
      };

      let savedEvent = initialEvent
        ? await mockBackend.events.updateEvent(initialEvent.id, userId, normalizedPayload)
        : await mockBackend.events.createEvent(userId, normalizedPayload);

      savedEvent = await mockBackend.events.submitEvent(savedEvent.id, userId);

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(storageKey);
      }
      setForm(defaultValues);
      setEditorKey((current) => current + 1);
      setFeedback({
        kind: 'success',
        message: 'Evento salvo e enviado para revisão.',
      });
      onSaved(savedEvent);
    } catch (error) {
      setFeedback({
        kind: 'error',
        message: userFacingError(error, 'Falha ao salvar o evento.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card bg-onyx border-0 shadow-lg">
      <div className="card-body p-4">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h5 className="mb-1 text-white">{formTitle}</h5>
            <p className="text-white-50 mb-0">
              O match fica disponível depois da aprovação administrativa do evento.
            </p>
          </div>
          {initialEvent && (
            <button type="button" className="btn btn-outline-light btn-sm" onClick={onCancelEdit}>
              Fechar edição
            </button>
          )}
        </div>

        {feedback && (
          <div className={`alert ${feedback.kind === 'success' ? 'alert-success' : 'alert-danger'} py-2`}>
            {feedback.message}
          </div>
        )}

        <form className="row g-3" onSubmit={handleSubmit}>
          <div className="col-12">
            <div className="section-title">Dados principais</div>
          </div>

          <LimitedTextInput
            id="event-title"
            label="Título"
            field="title"
            value={form.title}
            onChange={updateField}
            className="col-12"
          />

          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
              <label className="form-label text-white mb-0" htmlFor="event-description">
                Descrição
              </label>
              <div className="btn-group btn-group-sm toggle-group">
                <button
                  type="button"
                  className={`btn ${form.descriptionFormat === 'plain' ? 'btn-brand' : 'btn-outline-light'}`}
                  onClick={() => changeDescriptionFormat('plain')}
                >
                  Texto simples
                </button>
                <button
                  type="button"
                  className={`btn ${form.descriptionFormat === 'html' ? 'btn-brand' : 'btn-outline-light'}`}
                  onClick={() => changeDescriptionFormat('html')}
                >
                  Editor HTML
                </button>
              </div>
            </div>

            {form.descriptionFormat === 'plain' ? (
              <textarea
                id="event-description"
                className="form-control input-contrast"
                rows={4}
                value={form.description}
                onChange={(currentEvent) => updateField('description', currentEvent.target.value)}
                required
              />
            ) : (
              <div className="rich-editor">
                <div className="rich-editor-toolbar">
                  <button type="button" className="btn btn-outline-light btn-sm" onClick={() => applyRichCommand('bold')}>
                    B
                  </button>
                  <button type="button" className="btn btn-outline-light btn-sm" onClick={() => applyRichCommand('italic')}>
                    I
                  </button>
                  <button type="button" className="btn btn-outline-light btn-sm" onClick={() => applyRichCommand('underline')}>
                    U
                  </button>
                  <button type="button" className="btn btn-outline-light btn-sm" onClick={() => applyRichCommand('insertUnorderedList')}>
                    Lista
                  </button>
                  <button type="button" className="btn btn-outline-light btn-sm" onClick={insertLink}>
                    Link
                  </button>
                  <button type="button" className="btn btn-outline-light btn-sm" onClick={insertImage}>
                    Imagem
                  </button>
                </div>
                <div
                  key={editorKey}
                  ref={richEditorRef}
                  className="rich-editor-surface input-contrast"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncRichDescription}
                  onBlur={syncRichDescription}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeEventDescriptionHTML(form.description),
                  }}
                />
              </div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label text-white" htmlFor="event-startAt">
              Início
            </label>
            <input
              id="event-startAt"
              className="form-control input-contrast"
              type="datetime-local"
              value={form.startAt}
              onChange={(currentEvent) => updateField('startAt', currentEvent.target.value)}
              required
            />
          </div>

          <div className="col-md-6">
            <label className="form-label text-white" htmlFor="event-endAt">
              Término
            </label>
            <input
              id="event-endAt"
              className="form-control input-contrast"
              type="datetime-local"
              value={form.endAt}
              onChange={(currentEvent) => updateField('endAt', currentEvent.target.value)}
              required
            />
          </div>

          <LimitedTextInput
            id="event-location"
            label="Local"
            field="location"
            value={form.location}
            onChange={updateField}
            className="col-md-6"
          />

          <LimitedTextInput
            id="event-institution"
            label="Instituição"
            field="institution"
            value={form.institution}
            onChange={updateField}
            className="col-md-6"
            list="event-institution-list"
          />
          <datalist id="event-institution-list">
            {catalogs.institutions.map((institution) => (
              <option key={institution} value={institution} />
            ))}
          </datalist>

          <LimitedTextInput
            id="event-promoter"
            label="Promotor"
            field="promoter"
            value={form.promoter}
            onChange={updateField}
            className="col-md-6"
          />

          <div className="col-md-3">
            <label className="form-label text-white" htmlFor="event-format">
              Formato
            </label>
            <select
              id="event-format"
              className="form-select input-contrast"
              value={form.format}
              onChange={(currentEvent) =>
                updateField('format', currentEvent.target.value as EventFormValues['format'])
              }
            >
              <option value="presencial">Presencial</option>
              <option value="online">Online</option>
              <option value="hibrido">Híbrido</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label text-white" htmlFor="event-accessType">
              Acesso
            </label>
            <select
              id="event-accessType"
              className="form-select input-contrast"
              value={form.accessType}
              onChange={(currentEvent) =>
                updateField('accessType', currentEvent.target.value as EventFormValues['accessType'])
              }
            >
              <option value="aberto">Aberto</option>
              <option value="ingressos">Venda de ingressos</option>
            </select>
          </div>

          <LimitedTextInput
            id="event-targetAudience"
            label="Público"
            field="targetAudience"
            value={form.targetAudience}
            onChange={updateField}
            className="col-md-6"
            list="event-audience-list"
          />
          <datalist id="event-audience-list">
            {catalogs.audiences.map((audience) => (
              <option key={audience} value={audience} />
            ))}
          </datalist>

          <div className="col-md-3">
            <label className="form-label text-white" htmlFor="event-estimatedAudience">
              Estimativa de público
            </label>
            <input
              id="event-estimatedAudience"
              className="form-control input-contrast"
              type="number"
              min={1}
              step={1}
              value={form.estimatedAudience}
              onChange={(currentEvent) =>
                updateField('estimatedAudience', Number(currentEvent.target.value))
              }
              required
            />
          </div>

          <div className="col-md-9">
            <label className="form-label text-white" htmlFor="event-registrationLink">
              Link de inscrição
            </label>
            <input
              id="event-registrationLink"
              className="form-control input-contrast"
              type="url"
              placeholder="https://..."
              autoComplete="url"
              inputMode="url"
              maxLength={EVENT_URL_FIELD_LIMIT}
              pattern="https?://.+"
              value={form.registrationLink}
              onChange={(currentEvent) => updateField('registrationLink', currentEvent.target.value)}
              onBlur={() => updateField('registrationLink', form.registrationLink.trim())}
            />
          </div>

          <div className="col-12">
            <div className="form-check selected-match-box">
              <input
                id="event-responsibility"
                className="form-check-input input-contrast"
                type="checkbox"
                checked={form.responsibilityAccepted}
                onChange={(currentEvent) =>
                  updateField('responsibilityAccepted', currentEvent.target.checked)
                }
                required
              />
              <label className="form-check-label text-white" htmlFor="event-responsibility">
                Declaro que sou responsável pelas informações enviadas e pela autorização de uso dos
                materiais anexados ou referenciados.
              </label>
            </div>
          </div>

          <div className="col-12 d-flex gap-2">
            <button type="submit" className="btn btn-brand flex-grow-1" disabled={submitting}>
              {submitting ? 'Enviando...' : initialEvent ? 'Atualizar e reenviar' : 'Criar e enviar'}
            </button>
            {initialEvent && (
              <button type="button" className="btn btn-outline-light" onClick={onCancelEdit}>
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;
